import { useState, useEffect, useRef, useMemo } from "react";
import { GardienBadge } from "@/components/GardienBadge";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { LOGO } from "@/data/exercises";
import SpeakButton from "@/components/patient/SpeakButton";
import HighlightedStory, { splitStory, sentencesOf, sentenceAtFraction, sentenceStartFraction } from "@/components/patient/HighlightedStory";
import { AudioPlayer, PatientAudioPlayer, SentenceNav, FavoriteButton, SilentReader, TextSizeControl, savedTextScale } from "@/components/patient/StoryAudio";
import { useAuth } from "@/context/AuthContext";
import VoiceRecorder from "@/components/patient/VoiceRecorder";
import FableMorale from "@/components/FableMorale";
import FableSheetButton from "@/components/FableSheetButton";
import KeepStoryButton from "@/components/patient/KeepStoryButton";
import { getKept } from "@/lib/keptStories";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Feather, ScrollText, Loader2, ChevronLeft, Mic, X, Volume2, BookOpenText } from "lucide-react";

const KIND_LABEL = { conte: "Conte", fable: "Fable", poeme: "Poème" };

export default function Stories() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patient");
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [current, setCurrent] = useState(-1);
  const tokens = useMemo(() => splitStory(active?.content || ""), [active]);
  const sentences = useMemo(() => sentencesOf(tokens), [tokens]);
  const lead = active ? `${active.title}. ${active.author ? active.author + ". " : ""}` : "";
  const onProgress = useMemo(() => (f) => setCurrent(sentenceAtFraction(f, lead, sentences)), [lead, sentences]);
  const fractionOf = useMemo(() => (i) => sentenceStartFraction(i, lead, sentences), [lead, sentences]);
  const controllerRef = useRef(null);
  const deviceSpeak = active && (
    <SpeakButton text={`${lead}${active.content}`} parts={[lead, ...sentences]}
      onPart={(i) => setCurrent(i == null ? -1 : i - 1)} controllerRef={controllerRef}
      label="Lire à voix haute" testId="story-speak-button" />
  );
  const keptAudio = active && (() => { const k = getKept().find((x) => x.id === active.id); return k ? k.audio_url || k.arsene_url : null; })();
  const [silent, setSilent] = useState(false);
  const [scale, setScale] = useState(savedTextScale);
  useEffect(() => { setCurrent(-1); setSilent(false); setScale(savedTextScale()); }, [active]);
  useEffect(() => { setCurrent(-1); }, [silent]);

  const { user } = useAuth();
  const favBase = patientId ? `/patients/${patientId}/favorites` : user?.role === "family" ? "/family/favorites" : null;
  const [favs, setFavs] = useState([]);
  const recBase = patientId ? `/patients/${patientId}` : user?.role === "family" ? "/family" : null;
  const [recs, setRecs] = useState([]);
  const [source, setSource] = useState("default");
  const [recording, setRecording] = useState(false);
  useEffect(() => {
    setSource("default"); setRecording(false); setRecs([]);
    if (recBase && active) api.get(`${recBase}/recordings`, { params: { story_id: active.id } }).then((r) => setRecs(r.data))
      .catch(() => setRecs(getKept().find((x) => x.id === active.id)?.kept_recs || []));
  }, [recBase, active]);
  const removeRec = async (rec) => {
    if (!window.confirm(`Supprimer la lecture enregistrée par ${rec.label} ?`)) return;
    try {
      await api.delete(`${recBase}/recordings/${rec.id}`);
      setRecs((l) => l.filter((r) => r.id !== rec.id));
      if (source === rec.id) setSource("default");
      toast.success("Enregistrement supprimé");
    } catch { toast.error("Suppression impossible."); }
  };
  const chosenRec = recs.find((r) => r.id === source);
  const [arsene, setArsene] = useState(null);
  useEffect(() => { setArsene(null); }, [active]);
  useEffect(() => {
    if (source !== "arsene" || !active) return;
    let stop = false, timer;
    const poll = (retry) => api.get(`/stories/${active.id}/gardien-voice`, { params: { retry } }).then(({ data }) => {
      if (stop) return;
      setArsene(data);
      if (data.status === "pending") timer = setTimeout(() => poll(false), 4000);
    }).catch(() => {
      if (stop) return;
      const k = getKept().find((x) => x.id === active.id);
      setArsene(k?.arsene_url ? { status: "ready", url: k.arsene_url } : { status: "error" });
    });
    poll(true);
    return () => { stop = true; clearTimeout(timer); };
  }, [source, active]);
  useEffect(() => {
    if (favBase) api.get(favBase).then((r) => setFavs(r.data.story_ids)).catch(() => setFavs([]));
  }, [favBase]);
  const toggleFav = async (id) => {
    try {
      const r = await api.post(`${favBase}/${id}`);
      setFavs(r.data.story_ids);
      toast.success(r.data.story_ids.includes(id) ? "Ajouté aux contes préférés" : "Retiré des contes préférés");
    } catch { toast.error("Enregistrement impossible."); }
  };
  const goTo = (i) => {
    if (controllerRef.current) controllerRef.current.goTo(i);
    else toast("Appuyez d'abord sur la lecture.");
  };

  useEffect(() => {
    api.get("/stories").then((r) => setStories(r.data)).catch(() => { setStories(getKept()); setOffline(true); }).finally(() => setLoading(false));
  }, []);
  const [offline, setOffline] = useState(false);
  const [keptIds, setKeptIds] = useState(() => getKept().map((s) => s.id));
  const refreshKept = () => setKeptIds(getKept().map((s) => s.id));

  const filtered = stories
    .filter((s) => filter === "all" || (filter === "fav" ? favs.includes(s.id) : filter === "kept" ? keptIds.includes(s.id) : s.kind === filter))
    .sort((a, b) => Number(favs.includes(b.id)) - Number(favs.includes(a.id)));

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-care">
      <header className="bg-white/70 border-b border-amber-100 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Passerelle" className="h-10 w-auto" />
            <span className="text-gray-300">·</span>
            <span className="text-gray-500 text-sm font-semibold">Contes & Poésie</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => (active ? setActive(null) : navigate(-1))} data-testid="stories-back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-10 h-10 animate-spin text-amber-500" /></div>
        ) : active ? (
          <article className="soft-in" data-testid="story-reader">
            <button onClick={() => setActive(null)} data-testid="story-reader-back"
              className="inline-flex items-center gap-2 mb-6 text-emerald-700 font-patient font-bold text-lg">
              <ChevronLeft className="w-6 h-6" /> Retour à la liste
            </button>
            <div className="rounded-[2rem] bg-white border-4 border-amber-200 p-5 sm:p-12">
              <div className="inline-flex items-center gap-2 text-amber-700 font-patient font-bold mb-4">
                {active.kind === "poeme" ? <Feather className="w-5 h-5" /> : <ScrollText className="w-5 h-5" />}
                {KIND_LABEL[active.kind] || "Conte"}
              </div>

              <h1 className="font-serif-display font-normal text-gray-800 text-4xl sm:text-5xl mb-2 leading-tight" data-testid="story-title">{active.title}</h1>
              {active.author && <p className="font-patient text-gray-500 text-xl mb-6">{active.author}</p>}
              <div className="flex flex-wrap items-center gap-3">
                {favBase && <FavoriteButton big active={favs.includes(active.id)} onToggle={() => toggleFav(active.id)} testId="story-reader-favorite" />}
                <KeepStoryButton key={active.id} story={active} recs={recs} onChange={refreshKept} />
              </div>
              <div className="mt-6 inline-flex rounded-full bg-amber-50 border-2 border-amber-200 p-1" data-testid="story-mode-choice">
                {[[false, "Écouter", Volume2], [true, "Lire sans voix", BookOpenText]].map(([v, label, Icon]) => (
                  <button key={label} onClick={() => setSilent(v)} aria-pressed={silent === v} data-testid={v ? "story-mode-silent" : "story-mode-listen"}
                    className={`inline-flex items-center gap-2 h-11 px-5 rounded-full font-patient font-bold transition-colors ${silent === v ? "bg-amber-600 text-white" : "text-amber-800"}`}>
                    <Icon className="w-5 h-5" /> {label}
                  </button>
                ))}
              </div>
              {!silent && (
                <div className="mt-6" data-testid="story-voice-choice">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Choisir la voix</p>
                  <div className="flex flex-wrap gap-2">
                    {[{ id: "default", label: "Voix habituelle" }, { id: "arsene", label: "Voix d'Arsène" }, ...recs.map((r) => ({ id: r.id, label: `Voix de ${r.label}`, rec: r }))].map((o) => (
                      <span key={o.id} className={`inline-flex items-center rounded-full border-2 font-patient font-bold transition-colors ${source === o.id ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-emerald-200 text-emerald-800"}`}>
                        <button onClick={() => setSource(o.id)} className="h-11 pl-5 pr-4" data-testid={`story-voice-${o.id}`}>{o.label}</button>
                        {o.rec && (o.rec.mine || user?.role !== "family") && (
                          <button onClick={() => removeRec(o.rec)} className="h-11 pr-3 opacity-70 hover:opacity-100" aria-label="Supprimer" data-testid={`story-voice-delete-${o.id}`}><X className="w-4 h-4" /></button>
                        )}
                      </span>
                    ))}
                    {recBase && !recording && (
                      <button onClick={() => setRecording(true)} data-testid="story-record-button"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-full border-2 border-dashed border-rose-300 text-rose-500 font-patient font-bold hover:bg-rose-50 transition-colors">
                        <Mic className="w-5 h-5" /> Enregistrer ma voix
                      </button>
                    )}
                  </div>
                  {recording && (
                    <VoiceRecorder postUrl={`${recBase}/recordings`} fields={{ story_id: active.id }} defaultName={user?.name?.split(" ")[0]} onClose={() => setRecording(false)}
                      onSaved={(r) => { setRecs((l) => [r, ...l.filter((x) => x.id !== r.id)]); setSource(r.id); setRecording(false); }} />
                  )}
                </div>
              )}
              <div className="flex flex-col items-center gap-3 my-8">
                {silent ? (
                  <SilentReader key={active.id} sentences={sentences} current={current} setCurrent={setCurrent} controllerRef={controllerRef} />
                ) : source === "arsene" ? (
                  arsene?.status === "ready" ? (
                    <AudioPlayer key={arsene.url} src={arsene.url} onProgress={onProgress} controllerRef={controllerRef} fractionOf={fractionOf} />
                  ) : (
                    <div className="flex flex-col items-center gap-3 text-center" data-testid="story-arsene-status">
                      {arsene?.status === "error" || arsene?.status === "unavailable" ? (
                        <>
                          <p className="font-patient text-lg text-gray-600">La voix d'Arsène n'est pas disponible pour le moment. La tablette peut lire l'histoire avec sa propre voix :</p>
                          {deviceSpeak}
                        </>
                      ) : (
                        <>
                          <Loader2 className="w-10 h-10 animate-spin text-amber-600" />
                          <p className="font-patient font-bold text-lg text-amber-800">Arsène prépare sa lecture…</p>
                          <p className="text-sm text-gray-500">La première fois, cela peut prendre une minute.</p>
                        </>
                      )}
                    </div>
                  )
                ) : chosenRec ? (
                  <AudioPlayer key={chosenRec.id + chosenRec.audio_url} src={chosenRec.audio_url} onProgress={onProgress} controllerRef={controllerRef} fractionOf={fractionOf} />
                ) : patientId ? (
                  <PatientAudioPlayer key={active.id} patientId={patientId} onProgress={onProgress} controllerRef={controllerRef} fractionOf={fractionOf} fallback={keptAudio ? <AudioPlayer src={keptAudio} onProgress={onProgress} controllerRef={controllerRef} fractionOf={fractionOf} /> : deviceSpeak}
                    text={`${active.title}. ${active.author ? active.author + ". " : ""}${active.content}`} />
                ) : active.audio_url ? (
                  <AudioPlayer key={active.id} src={active.audio_url} onProgress={onProgress} controllerRef={controllerRef} fractionOf={fractionOf} />
                ) : (
                  deviceSpeak
                )}
              </div>
              <TextSizeControl scale={scale} setScale={setScale} />
              <HighlightedStory tokens={tokens} current={current} scale={scale} />
              {sentences.length > 1 && <SentenceNav current={current} total={sentences.length} onGo={goTo} />}
              <FableMorale story={active} />
              <div className="text-center"><FableSheetButton story={active} /></div>
            </div>
          </article>
        ) : (
          <div className="soft-in" data-testid="stories-list">
            <section className="relative rounded-3xl overflow-hidden border border-amber-100 shadow-[0_20px_40px_rgb(0,0,0,0.05)] mb-8" data-testid="stories-hero">
              <img src="/scenes/contes.jpg" alt="Arsène lit un conte au coin du feu" className="w-full aspect-[3/2] object-cover" data-testid="stories-hero-image" />
              <div className="sm:absolute sm:inset-y-0 sm:left-0 sm:w-[34%] flex flex-col justify-center p-6 sm:pl-8 sm:pr-2 bg-[#FBF6EC] sm:bg-transparent">
                <p className="text-xs uppercase tracking-[0.25em] text-amber-700 mb-2">Au coin du feu avec Arsène</p>
                <h1 className="font-serif-display text-4xl font-normal text-gray-800 mb-2 leading-tight">Contes & Poésie</h1>
                <p className="text-lg sm:text-base font-patient text-gray-600">Un moment de lecture douce, à lire ou à écouter.</p>
              </div>
            </section>
            {offline && (
              <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-4 mb-6 font-patient text-lg text-amber-900" data-testid="stories-offline-banner">
                Pas d'internet pour le moment : voici les contes gardés sur la tablette.
              </div>
            )}
            <GardienBadge id="arsene" eyebrow="Gardien des contes" className="max-w-xl mx-auto mb-8" testId="stories-gardien-arsene" />

            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {[["all", "Tout"], ...(favBase ? [["fav", "Favoris"]] : []), ...(keptIds.length ? [["kept", "Sur la tablette"]] : []), ["conte", "Contes"], ["fable", "Fables"], ["poeme", "Poèmes"]].map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`stories-filter-${k}`}
                  className={`px-5 h-11 rounded-full font-patient font-bold text-lg transition-colors ${filter === k ? "bg-emerald-700 text-white" : "bg-white border-2 border-emerald-200 text-emerald-700"}`}>
                  {label}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="rounded-3xl bg-white border-4 border-dashed border-gray-300 p-12 text-center" data-testid="stories-empty">
                <p className="text-2xl font-patient font-bold text-gray-400">{offline ? "Aucun conte gardé sur la tablette" : "Contenu à venir"}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {filtered.map((s) => (
                  <div key={s.id} className="relative">
                  {favBase && (
                    <div className="absolute top-4 right-4 z-[1]">
                      <FavoriteButton active={favs.includes(s.id)} onToggle={() => toggleFav(s.id)} testId={`story-favorite-${s.id}`} />
                    </div>
                  )}
                  <button onClick={() => setActive(s)} data-testid={`story-card-${s.id}`}
                    className="w-full h-full text-left rounded-3xl bg-white border-4 border-black/5 p-6 pr-16 active:scale-[0.98] transition-transform shadow-sm">
                    <span className="inline-flex items-center gap-2 text-amber-700 font-patient font-bold mb-2">
                      {s.kind === "poeme" ? <Feather className="w-5 h-5" /> : <ScrollText className="w-5 h-5" />}
                      {KIND_LABEL[s.kind] || "Conte"}{s.audio_url ? " · audio" : ""}{keptIds.includes(s.id) ? " · sur la tablette" : ""}
                    </span>
                    <h2 className="font-serif-display font-normal text-gray-800 text-2xl mb-1 leading-tight">{s.title}</h2>
                    {s.author && <p className="font-patient text-gray-500">{s.author}</p>}
                  </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
